import FadeIn from "@/app/components/ui/FadeIn";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { startCase } from "lodash";
import { Link } from "react-router-dom";
import { IStaticSidebarNavigationOptionProps } from "../../interface";
import StaticSidebarSubNavOption from "../SubNavOption";
import RBACFlag from "@/app/components/core/RBACFlag";
import { RouteKeys } from "@/api_framework/api_modals/Routers";

const NavOptionWrapper: React.FC<React.PropsWithChildren<{ url: string }>> = ({ children, url }) => {
  const navOptionClassname = clsx(
    "rounded-md w-full flex flex-col ",
    location.pathname.includes(url) ? " " : "text-gray-500",
  );
  return url !== "" ? (
    <li>
      {" "}
      <Link to={url} className={navOptionClassname} key={`${url}`}>
        {" "}
        {children}{" "}
      </Link>
    </li>
  ) : (
    <li>
      <div className={navOptionClassname}>{children}</div>
    </li>
  );
};

const StaticSidebarNavOption: React.FC<IStaticSidebarNavigationOptionProps> = ({
  navOption,
  primaryModuleKey,
  navIcons,
}) => {
  const { getRouteKey } = useRouter();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.SIDEBAR);
  const constants = useArrayConstants();
  const EXCEPTION_REDIRECTS = {
    CUSTOMER_PROFILE: getRouteKey("SEARCH_CUSTOMER", "url"),
  };
  const correctNavURL = () => {
    if (Object.keys(EXCEPTION_REDIRECTS).includes(navOption?.id)) {
      return EXCEPTION_REDIRECTS[navOption?.id];
    }
    return navOption?.url;
  };

  const NO_ARROW_NAV_OPTION_IDS = new Set([
    "ORDER_MANAGEMENT",
    "PATIENT_WAITING_LIST",
    "AT_HOME_SERVICES",
    "STAFF_CALENDAR",
    "LAB_REPORT",
    "DAY_CLOSING",
    "LAB_REPORT_INTERPRETATION",
  ]);

  return (
    !constants.excludedNavs.includes(primaryModuleKey) && (
      <NavOptionWrapper url={correctNavURL()}>
        <Disclosure>
          {({ open }) => (
            <>
              <Disclosure.Button
                onClick={() =>
                  logVMSClick(VMS_CLICK_EVENTS.SIDEBAR_MODULE_CLICK, startCase(navOption.title.split("_")?.join(" ")), {
                    nav_module_id: navOption.id,
                    event_action: open ? "COLLAPSE" : "EXPAND",
                  })
                }
                className="flex justify-between items-center border-gray-800 hover:bg-[#508DA014]  p-1 border rounded-lg w-full font-medium text-left text-sm focus:outline-none"
              >
                <li className="flex justify-between items-center pr-2 w-full">
                  <div className="flex justify-start items-center gap-x-3 bg-gray-80 p-2 rounded-md font-semibold text-gray-300 text-[11px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm leading-6 group">
                    <span>{navIcons[primaryModuleKey]}</span>
                    <span>{startCase(navOption.title.split("_")?.join(" "))}</span>
                  </div>
                  {Object.keys(navOption.children).length > 0 &&
                    !NO_ARROW_NAV_OPTION_IDS.has(navOption.id) &&
                    (open ? (
                      <ChevronUpIcon className="w-4 h-4 text-white" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-white" />
                    ))}
                </li>
              </Disclosure.Button>
              {Object.keys(navOption.children).length > 0 && !NO_ARROW_NAV_OPTION_IDS.has(navOption.id) && open && (
                <FadeIn show={open}>
                  <StaticSidebarSubNavOption subNavs={navOption.children} />
                </FadeIn>
              )}
            </>
          )}
        </Disclosure>
      </NavOptionWrapper>
    )
  );
};

const RBAC_GATED_NAV_OPTION_ACTIONS: Record<string, RouteKeys> = {
  LAB_REPORT_INTERPRETATION: "LAB_REPORT_INTERPRETATION_LIST",
};

const StaticSidebarNavOptionWithRBAC: React.FC<IStaticSidebarNavigationOptionProps> = (props) =>
  RBAC_GATED_NAV_OPTION_ACTIONS[props.navOption?.id] ? (
    <RBACFlag actionId={RBAC_GATED_NAV_OPTION_ACTIONS[props.navOption.id]}>
      <StaticSidebarNavOption {...props} />
    </RBACFlag>
  ) : (
    <StaticSidebarNavOption {...props} />
  );

export default StaticSidebarNavOptionWithRBAC;
